import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack, Section } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import { formatCurrency } from 'tio-reimagined/format';
import Button from 'tio-reimagined/components/button';
import { t, type IntlService } from 'ember-intl';
import { on } from '@ember/modifier';
import {
  PhSparkle,
  PhArrowLeft,
  PhPencilSimple,
  PhUploadSimple,
  PhArrowsClockwise,
  PhBookOpen,
  PhFile,
  PhCurrencyDollar,
  PhGraduationCap,
} from 'ember-phosphor-icons';
import PageHero from 'tio-reimagined/components/page-hero';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import StatePill from 'tio-employee/components/reimagined/tas/state-pill';
import PhaseExplainer from 'tio-employee/components/reimagined/tas/phase-explainer';
import ApplicationStatusTracker from 'tio-employee/components/reimagined/tas/application-status-tracker';
import RejectionDetail from 'tio-employee/components/reimagined/tas/rejection-detail';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type { DataSourceInfo, LiveTasData } from 'tio-employee/services/reimagined-data';
import type TasApplicationModel from 'tio-common/models/tas-application';
import type RouterService from '@ember/routing/router-service';
import type TASCourseModel from 'tio-common/models/tas-course';

interface TasApplicationShowModel {
  tasData: LiveTasData;
  tasUsedAmount: { yearToDate: number; isLiveData: boolean };
  dataSourceInfo: DataSourceInfo;
  applicationId: string;
  application: TasApplicationModel;
}

interface TasApplicationShowSignature {
  Args: {
    model: TasApplicationShowModel;
  };
}

const DRAFT_STATES = ['TAS.ApplicationState.CREATED', 'TAS.ApplicationState.DEFINE_COURSES'];
const COURSE_REJECTION_STATES = [
  'TAS.ApplicationState.COURSES_NOT_APPROVED',
  'TAS.ApplicationState.COURSES_REJECTED',
];
const EVIDENCE_REJECTION_STATES = [
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
  'TAS.ApplicationState.EVIDENCE_REJECTED',
];
const EVIDENCE_UPLOAD_STATES = [
  'TAS.ApplicationState.COURSES_APPROVED',
  'TAS.ApplicationState.ATTEND',
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
];

export default class CareerPathwaysTasApplicationShow extends Component<TasApplicationShowSignature> {
  @service declare intl: IntlService;
  @service declare reimaginedData: ReimaginedDataService;
  @service declare router: RouterService;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get tasSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.args.model.tasData.isLiveData ? 'live' : 'placeholder';
  }

  get application() {
    return this.args.model.application;
  }

  get programTemplate() {
    return this.application.tasProgramInstance?.tasProgramTemplate;
  }

  get programName() {
    return this.programTemplate?.programName || '';
  }

  get courses() {
    return this.application.activeCourses || [];
  }

  get totalDocuments() {
    const appAssets = this.application.tasAssets?.length || 0;
    const courseAssets = this.courses.reduce(
      (sum, course) => sum + (course.tasAssets?.length || 0),
      0
    );
    return appAssets + courseAssets;
  }

  get isRejected() {
    return (
      COURSE_REJECTION_STATES.includes(this.application.state) ||
      EVIDENCE_REJECTION_STATES.includes(this.application.state)
    );
  }

  get isDraft() {
    return DRAFT_STATES.includes(this.application.state);
  }

  get canUploadEvidence() {
    return EVIDENCE_UPLOAD_STATES.includes(this.application.state);
  }

  get approvedTotal() {
    return this.application.approvedTotal || 0;
  }

  courseDisplayName = (course: TASCourseModel) => {
    return (
      course.fields?.COURSE_NAME?.values?.[0] ||
      this.intl.t('tuition_assistance.reimagined.wizard.course.unnamed')
    );
  };

  courseAmountFormatted = (course: TASCourseModel) => {
    const tuition = Number(course.fields?.COURSE_TUITION?.values?.[0] || 0);
    if (!tuition) return '';
    return formatCurrency(tuition);
  };

  navigateToWizard = () => {
    const typeClass = this.programTemplate?.typeClassification;
    let routeSuffix = 'type-two';
    if (typeClass === 'TAS.ProgramType.3') {
      routeSuffix = 'type-three';
    } else if (typeClass?.startsWith('TAS.ProgramType.4')) {
      routeSuffix = 'type-four';
    }
    this.router.transitionTo(
      `authenticated.reimagined.tas.applications.${routeSuffix}.new`,
      this.args.model.applicationId
    );
  };

  <template>
    <VStack class="gap-6">
      {{! Back link }}
      <LinkTo
        @route="authenticated.reimagined.tas.applications"
        class="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
      >
        <PhArrowLeft @size={{16}} />
        {{t "tuition_assistance.reimagined.application_detail.back_to_applications"}}
      </LinkTo>

      {{! Hero section }}
      <PageHero @theme="fuchsia" @rounded="2xl" class="shadow-lg">
        <:default>
          <div class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div class="max-w-2xl">
              <div
                class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-white/80"
              >
                <PhSparkle class="h-4 w-4" />
                {{t "tuition_assistance.reimagined.application_detail.hero_eyebrow"}}
              </div>
              <Heading @level={{1}} class="text-white mt-1">
                {{this.application.displayName}}
              </Heading>
              {{#if this.programName}}
                <Text class="text-white/80 mt-1">{{this.programName}}</Text>
              {{/if}}
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <StatePill @state={{this.application.state}} />
              <StatePill @state={{this.application.paymentState}} />
            </div>
          </div>

          {{! Key metrics row }}
          <div class="mt-4 flex flex-wrap gap-6">
            {{#if this.courses.length}}
              <div class="flex items-center gap-2 text-white/90">
                <PhBookOpen @size={{16}} />
                <span class="text-sm">
                  {{t
                    "tuition_assistance.reimagined.application_detail.course_count"
                    count=this.courses.length
                  }}
                </span>
              </div>
            {{/if}}
            <div class="flex items-center gap-2 text-white/90">
              <PhFile @size={{16}} />
              <span class="text-sm">
                {{t
                  "tuition_assistance.reimagined.application_detail.document_count"
                  count=this.totalDocuments
                }}
              </span>
            </div>
            {{#if this.approvedTotal}}
              <div class="flex items-center gap-2 text-white/90">
                <PhCurrencyDollar @size={{16}} />
                <span class="text-sm">
                  {{t
                    "tuition_assistance.reimagined.application_detail.amount_approved"
                    amount=(formatCurrency this.approvedTotal)
                  }}
                </span>
              </div>
            {{/if}}
          </div>
        </:default>
      </PageHero>

      <DataIndicator
        @source={{this.tasSource}}
        @showBadge={{true}}
        @dataSourceId="tas-application-show"
      >
        <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {{! Main content (left 2/3) }}
          <div class="lg:col-span-2 flex flex-col gap-6">
            {{! Phase explainer }}
            <PhaseExplainer @state={{this.application.state}} />

            {{! Rejection detail (if applicable) }}
            {{#if this.isRejected}}
              <RejectionDetail @application={{this.application}} @onFix={{this.navigateToWizard}} />
            {{/if}}

            {{! Status tracker timeline }}
            <ApplicationStatusTracker @application={{this.application}} />

            {{! Courses section }}
            {{#if this.courses.length}}
              <Section>
                <:header>
                  <div class="flex items-center gap-2">
                    <PhGraduationCap @size={{20}} class="text-indigo-600" />
                    <Heading @level={{3}}>
                      {{t "tuition_assistance.reimagined.application_detail.section_courses"}}
                    </Heading>
                  </div>
                </:header>
                <:body>
                  <div class="grid grid-cols-1 gap-3">
                    {{#each this.courses as |course|}}
                      <div
                        class="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800"
                      >
                        <div class="min-w-0">
                          <div class="font-medium text-gray-900 dark:text-gray-100 truncate">
                            {{this.courseDisplayName course}}
                          </div>
                          {{#if (courseCredits course)}}
                            <Text class="text-xs" @intent="muted">
                              {{courseCredits course}}
                              {{t "tuition_assistance.reimagined.wizard.course.credits"}}
                            </Text>
                          {{/if}}
                        </div>
                        {{#if (this.courseAmountFormatted course)}}
                          <div
                            class="flex-shrink-0 font-semibold text-indigo-600 dark:text-indigo-400"
                          >
                            {{this.courseAmountFormatted course}}
                          </div>
                        {{/if}}
                      </div>
                    {{/each}}
                  </div>
                </:body>
              </Section>
            {{/if}}
          </div>

          {{! Sidebar (right 1/3) }}
          <div class="flex flex-col gap-6">
            {{! Quick actions }}
            <div
              class="rounded-2xl ring-1 ring-gray-950/[0.04] bg-white p-5 shadow-sm dark:ring-gray-700 dark:bg-gray-900"
            >
              <Heading @level={{4}} class="mb-4">
                {{t "tuition_assistance.reimagined.application_detail.section_actions"}}
              </Heading>
              <div class="flex flex-col gap-3">
                {{#if this.isDraft}}
                  <Button @intent="primary" class="w-full" {{on "click" this.navigateToWizard}}>
                    <PhPencilSimple @size={{16}} class="mr-2" />
                    {{t "tuition_assistance.reimagined.application_detail.action_edit_draft"}}
                  </Button>
                {{/if}}

                {{#if this.canUploadEvidence}}
                  <Button @intent="primary" class="w-full" {{on "click" this.navigateToWizard}}>
                    <PhUploadSimple @size={{16}} class="mr-2" />
                    {{t "tuition_assistance.reimagined.application_detail.action_upload_evidence"}}
                  </Button>
                {{/if}}

                {{#if this.isRejected}}
                  <Button @intent="primary" class="w-full" {{on "click" this.navigateToWizard}}>
                    <PhArrowsClockwise @size={{16}} class="mr-2" />
                    {{t "tuition_assistance.reimagined.application_detail.action_resubmit"}}
                  </Button>
                {{/if}}

                {{#unless this.isDemoMode}}
                  <LinkTo
                    @route="authenticated.tas.applications.show"
                    @model={{@model.applicationId}}
                    class="w-full"
                  >
                    <Button @appearance="outlined" class="w-full">
                      {{t "tuition_assistance.reimagined.application_detail.action_view_legacy"}}
                    </Button>
                  </LinkTo>
                {{/unless}}
              </div>
            </div>

            {{! Documents summary }}
            <div
              class="rounded-2xl ring-1 ring-gray-950/[0.04] bg-white p-5 shadow-sm dark:ring-gray-700 dark:bg-gray-900"
            >
              <div class="flex items-center gap-2 mb-3">
                <PhFile @size={{20}} class="text-indigo-600" />
                <Heading @level={{4}}>
                  {{t "tuition_assistance.reimagined.application_detail.section_documents"}}
                </Heading>
              </div>
              <Text @intent="muted" class="text-sm">
                {{t
                  "tuition_assistance.reimagined.application_detail.document_count"
                  count=this.totalDocuments
                }}
              </Text>
            </div>
          </div>
        </div>
      </DataIndicator>
    </VStack>
  </template>
}

// Helper for reading course field values
function courseCredits(course: TASCourseModel) {
  return course.fields?.COURSE_CREDIT?.values?.[0];
}
